import { useTranslation } from 'react-i18next';

interface SkillBadgeProps {
  skill: string;
  matched: boolean;
  /** When true the badge is styled as "nice to have" rather than required. */
  optional?: boolean;
  /** Show a tooltip with extra context */
  title?: string;
}

/**
 * SkillBadge displays a skill tag colour-coded by match status.
 *
 * - Green  (matched required)     — skill present in candidate CV
 * - Amber  (matched nice-to-have) — optional skill present in candidate CV
 * - Red    (missing required)     — skill absent from candidate CV
 * - Gray   (missing nice-to-have) — optional skill absent from candidate CV
 *
 * Colour contrast meets WCAG 2.1 AA (4.5:1 for text, 3:1 for UI components).
 */
export default function SkillBadge({
  skill,
  matched,
  optional = false,
  title,
}: SkillBadgeProps) {
  const { t } = useTranslation();
  let colorClasses: string;
  let srLabel: string;

  if (matched && !optional) {
    colorClasses = 'bg-green-100 text-green-800 ring-1 ring-green-300';
    srLabel = t('skillBadge.matchedRequired');
  } else if (matched && optional) {
    colorClasses = 'bg-amber-100 text-amber-800 ring-1 ring-amber-300';
    srLabel = t('skillBadge.matchedOptional');
  } else if (!matched && !optional) {
    colorClasses = 'bg-red-100 text-red-700 ring-1 ring-red-300';
    srLabel = t('skillBadge.missingRequired');
  } else {
    colorClasses = 'bg-gray-100 text-gray-600 ring-1 ring-gray-300';
    srLabel = t('skillBadge.missingOptional');
  }

  return (
    <span
      role="listitem"
      title={title}
      className={[
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
        colorClasses,
      ].join(' ')}
    >
      {/* Accessible indicator icon */}
      <span aria-hidden="true" className="text-[10px] leading-none">
        {matched ? '✓' : '✗'}
      </span>
      {skill}
      {/* Screen-reader-only status */}
      <span className="sr-only">({srLabel})</span>
    </span>
  );
}
